import {
  AddIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DeleteIcon,
  EditIcon,
  TimeIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactSelect from 'react-select';
import {
  ProviderSearchResult,
  ProviderSearchSelect,
} from '../../../components/provider-search';
import FormattedText from '../../../components/rich-text-editor/FormattedText';
import RichTextEditor from '../../../components/rich-text-editor/RichTextEditor';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  createProviderAbsence,
  deleteProviderAbsence,
  getProviderAbsences,
  updateProviderAbsence,
} from '../../../services/absences/providerAbsenceService';
import { simpleDeduplication } from '../../../services/absences/requestDeduplication';
import {
  ProviderAbsence,
  ProviderAbsenceCreatePayload,
  ProviderAbsenceUpdatePayload,
} from '../../../types/absences/types';
import { analytics } from '../../../utils/analytics';
import { validateAbsenceDates } from '../../../utils/dateValidation';
import { shouldShowCard } from '../../../utils/permissionUtils';
import { formatDateInPracticeTimezone } from '../../../utils/timezoneAwareDateFormatter';

interface ProviderAbsencesCardProps {
  providerId: string;
  practices: {
    id: string;
    name: string;
    timezone?: string | null;
    state?: string | null;
  }[];
  absences?: ProviderAbsence[];
  loading?: boolean;
  error?: string | null;
  refreshAbsences?: () => Promise<void>;
}

interface TimeOption {
  label: string;
  value: string;
}

interface CoveringProviderMetadata {
  providerId: string;
  firstName?: string;
  lastName?: string;
  providerLabel?: string;
}

// Generate time options in 15-minute increments (like OfficeHoursInput)
const generateTimeOptions = (): TimeOption[] => {
  const times: TimeOption[] = [];

  // Add 12:00 AM to 11:45 AM
  for (let hour = 0; hour <= 11; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'AM';
      const displayHour = hour === 0 ? 12 : hour;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  // Add 12:00 PM to 11:45 PM
  for (let hour = 12; hour <= 23; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const meridiem = 'PM';
      const displayHour = hour === 12 ? 12 : hour - 12;
      const timeString = `${displayHour}:${minute
        .toString()
        .padStart(2, '0')} ${meridiem}`;
      times.push({
        label: timeString,
        value: timeString,
      });
    }
  }

  return times;
};

const timeOptions = generateTimeOptions();

// Date chip component for consistent date display
const DateChip: React.FC<{
  label: string;
  value: string;
  icon: React.ComponentType<{ boxSize?: number | string }>;
  variant?: 'default' | 'scheduled' | 'expired';
}> = ({ label, value, icon: IconComponent, variant = 'default' }) => {
  const getVariantStyles = () => {
    switch (variant) {
      case 'scheduled':
        return {
          bg: 'yellow.50',
          color: 'yellow.700',
          borderColor: 'yellow.200',
        };
      case 'expired':
        return {
          bg: 'gray.50',
          color: 'gray.600',
          borderColor: 'gray.200',
        };
      default:
        return {
          bg: 'white',
          color: 'gray.700',
          borderColor: 'gray.200',
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <HStack
      spacing={1}
      px={2}
      py={1}
      borderRadius="md"
      border="1px solid"
      bg={styles.bg}
      color={styles.color}
      borderColor={styles.borderColor}
      fontSize="0.85rem"
      fontWeight="500"
    >
      <IconComponent boxSize={3} />
      <Text fontSize="0.85rem" fontWeight="500">
        {label}: {value}
      </Text>
    </HStack>
  );
};

// Helper function to format date consistently with practice timezone
const formatDate = (
  dateString: string,
  practiceId?: string,
  practices?: {
    id: string;
    name: string;
    timezone?: string | null;
    state?: string | null;
  }[],
): string => {
  // Find practice timezone information
  const practice = practices?.find(
    (p: {
      id: string;
      name: string;
      timezone?: string | null;
      state?: string | null;
    }) => p.id === practiceId,
  );
  const timezone = practice?.timezone;
  const state = practice?.state;

  return formatDateInPracticeTimezone(dateString, timezone, state);
};

// Helper function to format time consistently
const formatTime = (timeString: string): string => {
  return timeString; // Already in 12-hour format
};

// Note preview component with fade gradient and expand functionality
const NotePreview: React.FC<{
  content: string;
  maxHeight?: string;
}> = ({ content, maxHeight = '4rem' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!content || content.trim() === '') return null;

  const hasOverflow = content.length > 400; // Overflow detection

  return (
    <Box position="relative">
      <Box
        maxHeight={isExpanded ? 'none' : maxHeight}
        overflow={isExpanded ? 'visible' : 'auto'}
        position="relative"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e0',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a0aec0',
          },
        }}
      >
        <FormattedText
          content={content}
          fontSize="0.875rem"
          lineHeight="1.5"
          spacing="0.5rem"
        />

        {!isExpanded && hasOverflow && (
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            height="2rem"
            background="linear-gradient(transparent, white)"
            pointerEvents="none"
            zIndex={1}
          />
        )}
      </Box>

      {hasOverflow && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          mt={2}
          fontSize="0.75rem"
          color="yellow.600"
          _hover={{ bg: 'yellow.50' }}
        >
          {isExpanded ? 'Show Less' : 'View Full Note'}
          <ChevronDownIcon
            style={{
              transform: isExpanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.2s',
            }}
            boxSize={3}
            ml={1}
          />
        </Button>
      )}
    </Box>
  );
};

// Helper function to convert time string to minutes for comparison
const convertTimeToMinutes = (timeString: string): number => {
  if (!timeString) return 0;

  const match = timeString.match(/(\d+):(\d+)\s*(AM|PM)/);
  if (!match) return 0;

  let hour = parseInt(match[1]);
  const minute = parseInt(match[2]);
  const meridiem = match[3];

  // Convert to 24-hour format
  if (meridiem === 'PM' && hour !== 12) {
    hour += 12;
  } else if (meridiem === 'AM' && hour === 12) {
    hour = 0;
  }

  return hour * 60 + minute;
};

// Helper function to detect overnight shifts
const isOvernightShift = (
  startTime: string,
  endTime: string,
): boolean => {
  if (!startTime || !endTime) return false;

  // Parse time components
  const startMatch = startTime.match(/(\d+):(\d+)\s*(AM|PM)/);
  const endMatch = endTime.match(/(\d+):(\d+)\s*(AM|PM)/);

  if (!startMatch || !endMatch) return false;

  const startHour = parseInt(startMatch[1]);
  const endHour = parseInt(endMatch[1]);
  const startMeridiem = startMatch[3];
  const endMeridiem = endMatch[3];

  // Overnight: start time is PM, end time is AM
  const startIsPM = startMeridiem === 'PM' && startHour !== 12;
  const endIsAM = endMeridiem === 'AM' && endHour !== 12;

  return startIsPM && endIsAM;
};

const ProviderAbsencesCard: React.FC<ProviderAbsencesCardProps> = ({
  providerId,
  practices,
  absences: preFetchedAbsences,
  loading: preFetchedLoading,
  error: preFetchedError,
  refreshAbsences: parentRefreshAbsences,
}) => {
  const [absences, setAbsences] = useState<ProviderAbsence[] | null>(
    null,
  );
  const [loadingAbsences, setLoadingAbsences] = useState(false);

  const [isAbsenceModalOpen, setIsAbsenceModalOpen] = useState(false);
  const [absenceFormData, setAbsenceFormData] = useState<
    ProviderAbsenceCreatePayload | ProviderAbsenceUpdatePayload
  >({
    id: '',
    practiceId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    displayDate: '',
    note: '',
  });
  // For new absences: support multiple practice selection
  const [selectedPracticeIds, setSelectedPracticeIds] = useState<
    string[]
  >([]);
  const [coveringProviders, setCoveringProviders] = useState<
    ProviderSearchResult[]
  >([]);
  const [baseMetadata, setBaseMetadata] = useState<
    Record<string, unknown>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<
    string | null
  >(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [absenceToDelete, setAbsenceToDelete] = useState<
    string | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const isInitialMount = useRef(true);
  const hasFetched = useRef(false);
  const currentRequestId = useRef(0);
  const toast = useToast();

  // Get user permissions
  const {
    canEditAbsences,
    canViewDisplayDate,
    canEditDisplayDate,
    isAdmin,
  } = usePermissions();

  // Memoize practiceIds to create stable dependency
  const practiceIds = useMemo(
    () => practices?.map((practice) => practice.id) || [],
    [practices],
  );

  // Memoize practices map for efficient lookup
  const practicesMap = useMemo(() => {
    const map = new Map<
      string,
      {
        id: string;
        name: string;
        timezone?: string | null;
        state?: string | null;
      }
    >();
    practices?.forEach((practice) => {
      if (practice?.id) {
        map.set(practice.id.trim(), practice);
      }
    });
    return map;
  }, [practices]);

  // Shared fetch logic with deduplication to prevent duplicate API calls
  const doFetchProviderAbsences = useCallback(
    async (forceRefresh = false) => {
      if (!providerId) return;
      if (hasFetched.current && !forceRefresh) return;

      const requestId = ++currentRequestId.current;
      if (isInitialMount.current) {
        setLoadingAbsences(true);
        isInitialMount.current = false;
      }

      try {
        // Create unique key for this request to prevent duplicates
        const requestKey = `absences:${providerId}:${practiceIds.join(
          ',',
        )}`;

        // Use deduplication to prevent duplicate API calls
        const response = await simpleDeduplication.deduplicateRequest(
          requestKey,
          () =>
            getProviderAbsences({
              providerIds: [providerId],
              practiceIds: practiceIds,
            }),
        );

        if (requestId === currentRequestId.current) {
          if (response.absences) {
            setAbsences(response.absences as ProviderAbsence[]);
          } else {
            setAbsences([]);
          }
          hasFetched.current = true;
        }
      } catch (error) {
        if (requestId === currentRequestId.current) {
          console.error('Error fetching provider absences:', error);
          setAbsences([]);
          // Don't set hasFetched to true on error to allow retry attempts
        }
      } finally {
        if (requestId === currentRequestId.current) {
          setLoadingAbsences(false);
        }
      }
    },
    [providerId, practiceIds],
  );

  // Manual refresh function for CRUD operations
  const refreshAbsences = useCallback(async () => {
    // If we have a parent refresh function, use it to refresh all absences
    if (parentRefreshAbsences) {
      await parentRefreshAbsences();
      return;
    }

    // Otherwise, use the local refresh logic
    // Trigger a forced re-fetch immediately
    await doFetchProviderAbsences(true);
  }, [parentRefreshAbsences, doFetchProviderAbsences]);

  useEffect(() => {
    // If we have pre-fetched absences, use them instead of making API calls
    if (preFetchedAbsences !== undefined) {
      setAbsences(preFetchedAbsences);
      setLoadingAbsences(preFetchedLoading || false);
      hasFetched.current = true;
      return;
    }

    // Reset fetch flag when provider or practice IDs change
    hasFetched.current = false;
    isInitialMount.current = true;

    // Only fetch if we have the necessary data and no pre-fetched data
    if (providerId && practiceIds.length > 0) {
      // Call shared fetch logic directly to avoid circular dependency
      doFetchProviderAbsences();
    } else if (providerId && practiceIds.length === 0) {
      // If we have a provider but no practices, set empty absences
      setAbsences([]);
      hasFetched.current = true;
      setLoadingAbsences(false);
    }

    // Cleanup function to reset fetch flag on unmount
    return () => {
      hasFetched.current = false;
      isInitialMount.current = true;
      currentRequestId.current = 0;
    };
  }, [
    providerId,
    practiceIds,
    doFetchProviderAbsences,
    preFetchedAbsences,
    preFetchedLoading,
  ]);

  // Handle pre-fetched errors - display in UI
  useEffect(() => {
    if (preFetchedError) {
      console.error('Pre-fetched absences error:', preFetchedError);
      toast({
        title: 'Error loading absences',
        description:
          preFetchedError ||
          'Failed to load provider absences. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [preFetchedError, toast]);

  // Helper function to get practice ID from absence (handles both API formats)
  // Memoized to prevent unnecessary recalculations
  const getPracticeId = useCallback(
    (absence: ProviderAbsence): string | undefined => {
      // Use practiceIds array (new API format)
      if (
        Array.isArray(absence.practiceIds) &&
        absence.practiceIds.length > 0
      ) {
        return absence.practiceIds[0]?.trim() || undefined;
      }
      // Fallback to practiceId (old format for backward compatibility)
      return absence.practiceId?.trim() || undefined;
    },
    [],
  );

  // Helper function to get notes from absence (handles both API formats)
  const getNotes = useCallback(
    (absence: ProviderAbsence): string | null | undefined => {
      // Use note (new API format - singular)
      if (absence.note !== undefined && absence.note !== null) {
        return absence.note;
      }
      // Fallback to notes (old format for backward compatibility)
      return absence.notes;
    },
    [],
  );

  const sanitizeMetadataForSave = useCallback(
    (metadata?: Record<string, unknown>): Record<string, unknown> => {
      if (!metadata) return {};
      const sanitized = { ...metadata };
      delete sanitized.relatedAbsenceIds;
      delete sanitized.coveringProviders;
      return sanitized;
    },
    [],
  );

  const getCoveringProvidersFromMetadata = useCallback(
    (metadata?: Record<string, unknown>): ProviderSearchResult[] => {
      const providers = metadata?.coveringProviders;
      if (!Array.isArray(providers)) return [];

      return providers
        .map((provider) => {
          if (!provider || typeof provider !== 'object') return null;
          const p = provider as {
            providerId?: string;
            id?: string;
            firstName?: string;
            lastName?: string;
            providerLabel?: string;
            label?: string;
          };
          const id = p.providerId || p.id || '';
          const label =
            `${p.firstName || ''} ${p.lastName || ''}`.trim() ||
            p.providerLabel ||
            p.label ||
            '';
          if (!id) return null;
          return {
            id,
            label: label || 'Unknown Provider',
          };
        })
        .filter((provider): provider is ProviderSearchResult =>
          Boolean(provider),
        );
    },
    [],
  );

  // Helper function to get all practice IDs from absence
  // Memoized to prevent unnecessary recalculations
  const getAllPracticeIds = useCallback(
    (absence: ProviderAbsence): string[] => {
      // Use practiceIds array (new API format)
      if (
        Array.isArray(absence.practiceIds) &&
        absence.practiceIds.length > 0
      ) {
        return absence.practiceIds
          .map((id) => id?.trim())
          .filter((id): id is string => Boolean(id));
      }
      // Fallback to practiceId (old format for backward compatibility)
      if (absence.practiceId?.trim()) {
        return [absence.practiceId.trim()];
      }
      return [];
    },
    [],
  );

  // Helper function to get practice name(s)
  // Uses memoized practicesMap for efficient lookup
  const getPracticeNames = useCallback(
    (absence: ProviderAbsence): string[] => {
      const absencePracticeIds = getAllPracticeIds(absence);

      // If we have practice IDs, look them up in the practices map
      if (absencePracticeIds.length > 0) {
        const names = absencePracticeIds
          .map((id) => {
            const practice = practicesMap.get(id);
            // Return practice name if found, otherwise return practice ID as fallback
            return practice?.name || id;
          })
          .filter((name): name is string => Boolean(name)); // Filter out any null/undefined values

        // If we have at least one name/ID, return it
        if (names.length > 0) {
          return names;
        }
      }

      // Fallback: use practiceName from API if available
      if (absence.practiceName?.trim()) {
        return [absence.practiceName.trim()];
      }

      // Last resort: use practiceId if available
      if (absence.practiceId?.trim()) {
        return [absence.practiceId.trim()];
      }

      // If no practice identifiers found, return empty array (will be handled by UI)
      return [];
    },
    [getAllPracticeIds, practicesMap],
  );

  const openAbsenceModal = useCallback(
    (absence: ProviderAbsence | null = null): void => {
      if (absence) {
        // Edit mode: handle both single and multiple practice absences
        const allPracticeIds = getAllPracticeIds(absence);
        const practiceId =
          allPracticeIds.length > 0
            ? allPracticeIds[0]
            : getPracticeId(absence) || '';
        const notes = getNotes(absence) || '';

        setAbsenceFormData({
          id: absence.id,
          practiceId, // Keep for backward compatibility
          startDate: absence.startDate,
          endDate: absence.endDate || '', // Convert null to empty string for form
          startTime: absence.startTime || '',
          endTime: absence.endTime || '',
          displayDate: absence.displayDate || '',
          note: notes || '',
        });

        setCoveringProviders(
          getCoveringProvidersFromMetadata(absence.metadata),
        );
        setBaseMetadata(sanitizeMetadataForSave(absence.metadata));

        // Pre-populate with all practice IDs from practiceIds array (new API format)
        // Ensure we always have at least one practice ID, fallback to practiceId if needed
        const initialPracticeIds =
          allPracticeIds.length > 0
            ? allPracticeIds
            : practiceId
              ? [practiceId]
              : [];
        setSelectedPracticeIds(initialPracticeIds);
      } else {
        // New absence: support multiple practice selection
        setAbsenceFormData({
          id: '',
          practiceId: '', // Keep for backward compatibility but won't be used for new absences
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          displayDate: '',
          note: '',
        });
        setCoveringProviders([]);
        setBaseMetadata({});
        // Auto-select first practice for convenience
        const firstPracticeId = practices?.[0]?.id;
        setSelectedPracticeIds(
          firstPracticeId ? [firstPracticeId] : [],
        );
      }
      setValidationError(null);
      setIsAbsenceModalOpen(true);
    },
    [
      getAllPracticeIds,
      getPracticeId,
      getNotes,
      getCoveringProvidersFromMetadata,
      sanitizeMetadataForSave,
      practices,
    ],
  );

  // Helper function to update form data
  const updateFormData = (field: string, value: string) => {
    setValidationError(null);
    setAbsenceFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Helper function to close modal and reset state
  const closeModal = () => {
    setIsAbsenceModalOpen(false);
    setValidationError(null);
    setAbsenceFormData({
      id: '',
      practiceId: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      displayDate: '',
      note: '',
    });
    setSelectedPracticeIds([]);
    setCoveringProviders([]);
    setBaseMetadata({});
  };

  // Helper function to ensure dates are saved in correct format (YYYY-MM-DD)
  const formatDateForAPI = (dateString: string): string => {
    if (!dateString) return '';

    // Parse the date string in YYYY-MM-DD format explicitly to avoid timezone issues
    const [yearStr, monthStr, dayStr] = dateString.split('-');
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (isNaN(year) || isNaN(month) || isNaN(day)) {
      return '';
    }
    return dateString;
  };

  const handleSaveAbsence = async (): Promise<void> => {
    // Determine which practices to use
    const isEditMode = 'id' in absenceFormData && absenceFormData.id;
    // In edit mode, use selectedPracticeIds (which should be pre-populated from practiceIds array)
    // In new mode, use selectedPracticeIds
    const practicesToProcess =
      selectedPracticeIds.length > 0
        ? selectedPracticeIds.filter((id) => id?.trim()) // Filter out empty strings
        : absenceFormData.practiceId?.trim()
          ? [absenceFormData.practiceId.trim()]
          : [];

    // Validate that at least one practice is selected
    if (
      !practicesToProcess.length ||
      practicesToProcess.every((id) => !id?.trim())
    ) {
      setValidationError(
        'Please select at least one practice before saving the absence.',
      );
      return;
    }

    // Prevent duplicate submissions
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const coveringProvidersMetadata: CoveringProviderMetadata[] =
        coveringProviders.map((provider) => {
          const label = provider.label?.trim() || '';
          const parts = label.split(/\s+/).filter(Boolean);
          const firstName = parts.shift() || '';
          const lastName = parts.join(' ');
          return {
            providerId: provider.id,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            providerLabel: label || undefined,
          };
        });
      const metadataForSave =
        isEditMode ||
        coveringProvidersMetadata.length > 0 ||
        Object.keys(baseMetadata).length > 0
          ? {
              ...baseMetadata,
              coveringProviders: coveringProvidersMetadata,
            }
          : undefined;

      if (isEditMode) {
        // Find the original absence to compare practice IDs
        const originalAbsence = absences?.find(
          (a) => a.id === absenceFormData.id,
        );

        // Normalize practice IDs for comparison (sort and trim)
        const normalizedSelectedIds = practicesToProcess
          .map((id) => id?.trim())
          .filter((id): id is string => Boolean(id))
          .sort();

        // Determine if we need to delete and recreate
        let shouldDeleteAndRecreate = false;

        if (originalAbsence) {
          const originalPracticeIds =
            getAllPracticeIds(originalAbsence);
          const normalizedOriginalIds = originalPracticeIds
            .map((id) => id?.trim())
            .filter((id): id is string => Boolean(id))
            .sort();

          // Check if practice IDs have changed
          const practiceIdsChanged =
            normalizedOriginalIds.length !==
              normalizedSelectedIds.length ||
            !normalizedOriginalIds.every(
              (id, index) => id === normalizedSelectedIds[index],
            );

          // If practice IDs have changed OR if multiple practices, delete and recreate
          // This handles:
          // - Single to single (different practice): delete and recreate
          // - Single to multiple: delete and recreate
          // - Multiple to single: delete and recreate
          // - Multiple to multiple (different set): delete and recreate
          // - Multiple to multiple (same set): delete and recreate (API doesn't support multi-practice updates)
          shouldDeleteAndRecreate =
            practiceIdsChanged || normalizedSelectedIds.length > 1;
        } else {
          // If original absence not found, delete and recreate to be safe
          shouldDeleteAndRecreate = true;
        }

        if (shouldDeleteAndRecreate) {
          // Delete old absence and create new ones
          await deleteProviderAbsence([absenceFormData.id]);

          // Create new absences for each selected practice
          const createPromises = practicesToProcess.map(
            (practiceId) => {
              const createPayload: ProviderAbsenceCreatePayload = {
                providerId,
                practiceId: practiceId.trim(),
                startDate: formatDateForAPI(
                  absenceFormData.startDate,
                ),
                endDate: absenceFormData.endDate
                  ? formatDateForAPI(absenceFormData.endDate)
                  : '',
                startTime: absenceFormData.startTime,
                endTime: absenceFormData.endTime,
                displayDate: absenceFormData.displayDate
                  ? formatDateForAPI(absenceFormData.displayDate)
                  : '',
                note: absenceFormData.note || undefined,
                metadata: metadataForSave,
              };
              return createProviderAbsence(createPayload);
            },
          );

          await Promise.all(createPromises);
        } else {
          // Single practice, no change in practice ID: use update API
          const updatePayload: ProviderAbsenceUpdatePayload = {
            id: absenceFormData.id,
            practiceId: practicesToProcess[0].trim(),
            startDate: formatDateForAPI(absenceFormData.startDate),
            endDate: absenceFormData.endDate
              ? formatDateForAPI(absenceFormData.endDate)
              : '',
            startTime: absenceFormData.startTime,
            endTime: absenceFormData.endTime,
            displayDate: absenceFormData.displayDate
              ? formatDateForAPI(absenceFormData.displayDate)
              : '',
            note: absenceFormData.note || undefined,
            metadata: metadataForSave,
          };
          await updateProviderAbsence(updatePayload);
        }
      } else {
        // Create new absences for each selected practice
        const createPromises = practicesToProcess.map(
          (practiceId) => {
            const createPayload: ProviderAbsenceCreatePayload = {
              providerId,
              practiceId: practiceId.trim(),
              startDate: formatDateForAPI(absenceFormData.startDate),
              endDate: absenceFormData.endDate
                ? formatDateForAPI(absenceFormData.endDate)
                : '',
              startTime: absenceFormData.startTime,
              endTime: absenceFormData.endTime,
              displayDate: absenceFormData.displayDate
                ? formatDateForAPI(absenceFormData.displayDate)
                : '',
              note: absenceFormData.note || undefined,
              metadata: metadataForSave,
            };
            return createProviderAbsence(createPayload);
          },
        );

        // Execute all create operations in parallel
        await Promise.all(createPromises);
      }

      // Refresh absences to get the updated data from server (as backup)
      await refreshAbsences();
      analytics.trackEvent('update', 'absence');

      closeModal();
      const practiceCount = practicesToProcess.length;
      toast({
        title: 'Success',
        description:
          practiceCount > 1
            ? `Absence saved for ${practiceCount} practices successfully.`
            : 'Absence saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to save absence:', error);
      toast({
        title: 'Error',
        description: 'Failed to save absence. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAbsence = async (
    absenceId: string,
  ): Promise<void> => {
    // Prevent duplicate submissions
    if (isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);

      // Find the absence to check if it has related IDs (merged absences)
      const absence = absences?.find((a) => a.id === absenceId);
      const relatedIds = absence?.metadata?.relatedAbsenceIds as
        | string[]
        | undefined;

      // If this absence was merged from multiple absences, delete all related IDs
      // relatedAbsenceIds includes the primary ID and all other merged IDs
      // If no relatedAbsenceIds, it's a single absence - just delete the one ID
      const idsToDelete =
        relatedIds &&
        Array.isArray(relatedIds) &&
        relatedIds.length > 0
          ? relatedIds
          : [absenceId];

      // Ensure we're sending an array of valid IDs
      const validIdsToDelete = idsToDelete.filter(
        (id): id is string => Boolean(id?.trim()),
      );

      if (validIdsToDelete.length === 0) {
        throw new Error('No valid absence IDs to delete');
      }

      await deleteProviderAbsence(validIdsToDelete);
      analytics.trackEvent('delete', 'absence');

      // Immediately remove the deleted absence from local state for instant UI update
      setAbsences((prevAbsences) =>
        prevAbsences
          ? prevAbsences.filter((absence) => absence.id !== absenceId)
          : [],
      );

      // Refresh absences to get the updated data from server
      await refreshAbsences();

      toast({
        title: 'Success',
        description: 'Absence deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to delete absence:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete absence. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Sort absences by start date (most recent first)
  const sortedAbsences = (absences || []).sort((a, b) => {
    const dateA = new Date(a.startDate);
    const dateB = new Date(b.startDate);
    return dateB.getTime() - dateA.getTime();
  });
  const [latestAbsence, ...olderAbsences] = sortedAbsences;

  // Helper function to render absence details
  const renderAbsenceDetails = (absence: ProviderAbsence) => {
    const practiceId = getPracticeId(absence); // Use first practice ID for date formatting
    const notes = getNotes(absence);
    const practiceNames = getPracticeNames(absence);
    const isMultiPractice = practiceNames.length > 1;
    const coveringProviderLabels = getCoveringProvidersFromMetadata(
      absence.metadata,
    ).map((provider) => provider.label);

    return (
      <VStack align="flex-start" spacing={2} flex={1}>
        {/* Practice Name(s) */}
        {isMultiPractice ? (
          <VStack align="flex-start" spacing={1}>
            <Text fontSize="md" fontWeight="600" color="gray.800">
              Multiple Practices
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {practiceNames.map((name, index) => (
                <Box
                  key={`${name}-${index}`}
                  px={2}
                  py={1}
                  borderRadius="md"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="blue.700"
                  >
                    {name}
                  </Text>
                </Box>
              ))}
            </HStack>
          </VStack>
        ) : (
          <Text fontSize="md" fontWeight="600" color="gray.800">
            {practiceNames.length > 0
              ? practiceNames[0]
              : getAllPracticeIds(absence).length > 0
                ? getAllPracticeIds(absence)[0]
                : 'Unknown Practice'}
          </Text>
        )}

        {coveringProviderLabels.length > 0 && (
          <VStack align="flex-start" spacing={1}>
            <Text fontSize="sm" fontWeight="600" color="gray.700">
              Covering Provider
              {coveringProviderLabels.length > 1 ? 's' : ''}
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {coveringProviderLabels.map((label, index) => (
                <Box
                  key={`${label}-${index}`}
                  px={2}
                  py={1}
                  borderRadius="md"
                  bg="green.50"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text
                    fontSize="0.875rem"
                    fontWeight="500"
                    color="green.700"
                  >
                    {label}
                  </Text>
                </Box>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Dates Row */}
        <HStack spacing={2} flexWrap="wrap">
          {absence.startDate && (
            <DateChip
              label="Start"
              value={
                absence.startTime
                  ? `${formatDate(
                      absence.startDate,
                      practiceId,
                      practices,
                    )} at ${formatTime(absence.startTime)}`
                  : formatDate(
                      absence.startDate,
                      practiceId,
                      practices,
                    )
              }
              icon={CalendarIcon}
            />
          )}
          {absence.endDate &&
          typeof absence.endDate === 'string' &&
          absence.endDate.trim() ? (
            <DateChip
              label="End"
              value={
                absence.endTime
                  ? `${formatDate(
                      absence.endDate,
                      practiceId,
                      practices,
                    )} at ${formatTime(absence.endTime)}`
                  : formatDate(absence.endDate, practiceId, practices)
              }
              icon={CalendarIcon}
            />
          ) : (
            <DateChip
              label="End"
              value="Indefinite"
              icon={CalendarIcon}
              variant="expired"
            />
          )}
          {absence.displayDate &&
            (canViewDisplayDate() ||
              canEditDisplayDate() ||
              isAdmin()) && (
              <DateChip
                label="Display"
                value={formatDate(
                  absence.displayDate,
                  practiceId,
                  practices,
                )}
                icon={TimeIcon}
                variant={
                  new Date(absence.displayDate) > new Date()
                    ? 'scheduled'
                    : 'default'
                }
              />
            )}
        </HStack>

        {/* Time Range Row - Only show for today's partial absences or overnight shifts */}
        {absence.startTime &&
          absence.endTime &&
          (() => {
            const startDate = new Date(absence.startDate);
            const endDate = absence.endDate
              ? new Date(absence.endDate)
              : startDate;
            const today = new Date();
            const isToday =
              startDate.toDateString() === today.toDateString() &&
              (absence.endDate
                ? endDate.toDateString() === today.toDateString()
                : false);
            const isOvernight = isOvernightShift(
              absence.startTime,
              absence.endTime,
            );

            // Only show time period for today's partial absences or overnight shifts
            if (isToday || isOvernight) {
              return (
                <VStack align="flex-start" spacing={2} flex={1}>
                  <HStack spacing={2} flexWrap="wrap">
                    {/* Time Period chip - only for today's partial absences */}
                    {isToday && (
                      <DateChip
                        label="Time Period"
                        value={`${formatTime(
                          absence.startTime,
                        )} - ${formatTime(absence.endTime)}`}
                        icon={TimeIcon}
                        variant="scheduled"
                      />
                    )}

                    {/* Overnight indicator */}
                    {isOvernight && (
                      <HStack
                        spacing={1}
                        px={2}
                        py={1}
                        borderRadius="md"
                        border="1px solid"
                        bg="purple.50"
                        color="purple.700"
                        borderColor="purple.200"
                      >
                        <Text fontSize="0.85rem" fontWeight="600">
                          🌙 Overnight
                        </Text>
                      </HStack>
                    )}
                  </HStack>

                  {/* Helper text for overnight shifts */}
                  {isOvernight && (
                    <Text
                      fontSize="0.75rem"
                      color="purple.600"
                      fontStyle="italic"
                    >
                      Provider unavailable from {absence.startTime} on{' '}
                      {formatDate(
                        absence.startDate,
                        practiceId,
                        practices,
                      )}
                      until {absence.endTime} on{' '}
                      {absence.endDate
                        ? formatDate(
                            absence.endDate,
                            practiceId,
                            practices,
                          )
                        : 'indefinite date'}
                    </Text>
                  )}
                </VStack>
              );
            }

            // For multi-day absences with times, don't show time period
            return null;
          })()}

        {/* Notes */}
        <NotePreview content={notes || ''} maxHeight="4rem" />
      </VStack>
    );
  };

  return (
    <Box
      padding="0.5rem 0.625rem"
      backgroundColor="yellow.100"
      borderRadius="0.5rem"
      width="100%"
      mt="0.25rem"
    >
      {loadingAbsences ? (
        <Flex align="center" justify="center">
          <Spinner size="md" color="yellow.800" />
          <Text ml="1rem">Loading Provider Absences...</Text>
        </Flex>
      ) : (
        <Stack spacing={4}>
          {latestAbsence &&
            shouldShowCard(latestAbsence.displayDate) && (
              <Box
                key={latestAbsence.id}
                padding="1rem"
                backgroundColor="yellow.50"
                border="1px solid"
                borderColor="yellow.200"
                borderRadius="0.75rem"
                display="flex"
                flexDirection="column"
                gap="0.75rem"
                _hover={{
                  boxShadow: 'md',
                  borderColor: 'yellow.300',
                }}
                transition="all 0.2s ease-in-out"
              >
                {/* Header Row */}
                <Flex
                  justify="flex-start"
                  align="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <HStack spacing={3} align="center">
                    <CalendarIcon
                      color="yellow.600"
                      boxSize="1.25rem"
                      flexShrink={0}
                    />
                    <Text
                      fontSize="1rem"
                      fontWeight="600"
                      color="gray.800"
                    >
                      Provider Absence
                    </Text>
                  </HStack>

                  {/* Actions - Will wrap to next line if needed */}
                  {(canEditAbsences() || isAdmin()) && (
                    <HStack
                      spacing={2}
                      minW="5rem"
                      justify="flex-end"
                      ml="auto"
                    >
                      <IconButton
                        aria-label="Edit Absence"
                        icon={<EditIcon />}
                        size="sm"
                        onClick={() =>
                          openAbsenceModal(latestAbsence)
                        }
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                      />
                      <IconButton
                        aria-label="Delete Absence"
                        icon={<DeleteIcon />}
                        size="sm"
                        onClick={() => {
                          setAbsenceToDelete(latestAbsence.id);
                          setIsDeleteModalOpen(true);
                        }}
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'gray.100' }}
                        isDisabled={isDeleting}
                      />
                    </HStack>
                  )}
                </Flex>

                {renderAbsenceDetails(latestAbsence)}
              </Box>
            )}

          {olderAbsences.length > 0 && (
            <Collapse in={!isCollapsed}>
              {olderAbsences
                .filter((absence) =>
                  shouldShowCard(absence.displayDate),
                )
                .map((absence) => (
                  <Box
                    key={absence.id}
                    padding="1rem"
                    backgroundColor="yellow.50"
                    border="1px solid"
                    borderColor="yellow.200"
                    borderRadius="0.75rem"
                    display="flex"
                    flexDirection="column"
                    gap="0.75rem"
                    mt="0.75rem"
                    _hover={{
                      boxShadow: 'md',
                      borderColor: 'yellow.300',
                    }}
                    transition="all 0.2s ease-in-out"
                  >
                    {/* Header Row */}
                    <Flex
                      justify="flex-start"
                      align="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <HStack spacing={3} align="center">
                        <CalendarIcon
                          color="yellow.600"
                          boxSize="1.25rem"
                          flexShrink={0}
                        />
                        <Text
                          fontSize="1rem"
                          fontWeight="600"
                          color="gray.800"
                        >
                          Provider Absence
                        </Text>
                      </HStack>

                      {/* Actions - Will wrap to next line if needed */}
                      {(canEditAbsences() || isAdmin()) && (
                        <HStack
                          spacing={2}
                          minW="5rem"
                          justify="flex-end"
                          ml="auto"
                        >
                          <IconButton
                            aria-label="Edit Absence"
                            icon={<EditIcon />}
                            size="sm"
                            onClick={() => openAbsenceModal(absence)}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                          />
                          <IconButton
                            aria-label="Delete Absence"
                            icon={<DeleteIcon />}
                            size="sm"
                            onClick={() => {
                              setAbsenceToDelete(absence.id);
                              setIsDeleteModalOpen(true);
                            }}
                            backgroundColor="white"
                            _hover={{ backgroundColor: 'gray.100' }}
                            isDisabled={isDeleting}
                          />
                        </HStack>
                      )}
                    </Flex>

                    {renderAbsenceDetails(absence)}
                  </Box>
                ))}
            </Collapse>
          )}

          {/* Add Absence and Show More buttons - will wrap to next line if needed */}
          <Flex
            justify="flex-start"
            align="center"
            flexWrap="wrap"
            gap={2}
          >
            {/* Show "No Data" message when no absences */}
            {(!absences || absences.length === 0) && (
              <Text
                fontSize="0.875rem"
                color="gray.500"
                fontStyle="italic"
                textAlign="center"
                width="100%"
                py="1rem"
              >
                No Provider Absences
              </Text>
            )}

            {/* Show Add button only for users with permissions */}
            {(canEditAbsences() || isAdmin()) && (
              <Button
                size="sm"
                leftIcon={<AddIcon />}
                onClick={() => openAbsenceModal(null)}
                backgroundColor="yellow.100"
                color="black"
                _hover={{ backgroundColor: 'yellow.200' }}
                isDisabled={!practices?.length}
                title={
                  !practices?.length
                    ? 'No practices available for this provider'
                    : 'Add a new absence'
                }
              >
                Add Provider Absence
              </Button>
            )}
            {olderAbsences.filter((absence) =>
              shouldShowCard(absence.displayDate),
            ).length > 0 && (
              <Button
                size="sm"
                leftIcon={
                  isCollapsed ? (
                    <ChevronDownIcon />
                  ) : (
                    <ChevronUpIcon />
                  )
                }
                onClick={() => setIsCollapsed(!isCollapsed)}
                backgroundColor="yellow.100"
                color="black"
                _hover={{ backgroundColor: 'yellow.200' }}
              >
                {isCollapsed
                  ? `Show ${
                      olderAbsences.filter((absence) =>
                        shouldShowCard(absence.displayDate),
                      ).length
                    } more`
                  : 'Hide older absences'}
              </Button>
            )}
          </Flex>
        </Stack>
      )}

      {/* Add/Edit Absence Modal */}
      <Modal
        isOpen={isAbsenceModalOpen}
        onClose={() => setIsAbsenceModalOpen(false)}
        size="6xl"
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent
          maxW="90vw"
          maxH="90vh"
          mx="auto"
          borderRadius="xl"
          boxShadow="2xl"
        >
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="yellow.50"
            borderTopRadius="xl"
          >
            <Text fontSize="1.25rem" fontWeight="600">
              Provider Absences
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p="2rem">
            <Box maxH="70vh" overflowY="auto" pr="0.5rem">
              <VStack spacing="1.5rem" align="stretch">
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Select Practice(s){' '}
                    <span style={{ color: 'red' }}>*</span>
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    {'id' in absenceFormData && absenceFormData.id
                      ? 'Select one or more practices where this provider will be absent. You can edit which practices are included.'
                      : 'Select one or more practices where this provider will be absent. You can create absences for multiple practices at once.'}
                  </Text>
                  {'id' in absenceFormData && absenceFormData.id ? (
                    // Edit mode: multi-select checkboxes (supports both single and multi-practice absences)
                    <VStack
                      align="stretch"
                      spacing={2}
                      p={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      maxH="200px"
                      overflowY="auto"
                    >
                      {practices?.length === 0 ? (
                        <Text
                          fontSize="0.875rem"
                          color="gray.500"
                          fontStyle="italic"
                        >
                          No practices available
                        </Text>
                      ) : (
                        practices?.map((practice) => (
                          <Checkbox
                            key={practice.id}
                            isChecked={selectedPracticeIds.includes(
                              practice.id,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPracticeIds((prev) => [
                                  ...prev,
                                  practice.id,
                                ]);
                                // Also update practiceId for backward compatibility
                                updateFormData(
                                  'practiceId',
                                  practice.id,
                                );
                              } else {
                                setSelectedPracticeIds((prev) =>
                                  prev.filter(
                                    (id) => id !== practice.id,
                                  ),
                                );
                                // Update practiceId to first remaining practice or empty
                                const remaining =
                                  selectedPracticeIds.filter(
                                    (id) => id !== practice.id,
                                  );
                                updateFormData(
                                  'practiceId',
                                  remaining[0] || '',
                                );
                              }
                              setValidationError(null);
                            }}
                            colorScheme="blue"
                            size="md"
                          >
                            <Text
                              fontSize="0.875rem"
                              color="gray.700"
                            >
                              {practice.name}
                            </Text>
                          </Checkbox>
                        ))
                      )}
                      {selectedPracticeIds.length > 0 && (
                        <Text
                          fontSize="0.75rem"
                          color="blue.600"
                          mt={2}
                          fontWeight="500"
                        >
                          {selectedPracticeIds.length} practice
                          {selectedPracticeIds.length > 1
                            ? 's'
                            : ''}{' '}
                          selected
                        </Text>
                      )}
                    </VStack>
                  ) : (
                    // New absence: multi-select checkboxes
                    <VStack
                      align="stretch"
                      spacing={2}
                      p={3}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      bg="white"
                      maxH="200px"
                      overflowY="auto"
                    >
                      {practices?.length === 0 ? (
                        <Text
                          fontSize="0.875rem"
                          color="gray.500"
                          fontStyle="italic"
                        >
                          No practices available
                        </Text>
                      ) : (
                        practices?.map((practice) => (
                          <Checkbox
                            key={practice.id}
                            isChecked={selectedPracticeIds.includes(
                              practice.id,
                            )}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedPracticeIds((prev) => [
                                  ...prev,
                                  practice.id,
                                ]);
                              } else {
                                setSelectedPracticeIds((prev) =>
                                  prev.filter(
                                    (id) => id !== practice.id,
                                  ),
                                );
                              }
                              setValidationError(null);
                            }}
                            colorScheme="blue"
                            size="md"
                          >
                            <Text
                              fontSize="0.875rem"
                              color="gray.700"
                            >
                              {practice.name}
                            </Text>
                          </Checkbox>
                        ))
                      )}
                      {selectedPracticeIds.length > 0 && (
                        <Text
                          fontSize="0.75rem"
                          color="blue.600"
                          mt={2}
                          fontWeight="500"
                        >
                          {selectedPracticeIds.length} practice
                          {selectedPracticeIds.length > 1
                            ? 's'
                            : ''}{' '}
                          selected
                        </Text>
                      )}
                    </VStack>
                  )}
                </Box>

                <HStack align="flex-end" spacing="1rem">
                  <Box flex="1">
                    <Text
                      fontSize="0.875rem"
                      mb="0.5rem"
                      fontWeight="500"
                      color="gray.700"
                    >
                      Start Date{' '}
                      <span style={{ color: 'red' }}>*</span>
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      color="gray.600"
                      mb="0.5rem"
                    >
                      When this absence begins. Can be in the past.
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        type="date"
                        value={absenceFormData.startDate}
                        onChange={(e) => {
                          updateFormData('startDate', e.target.value);
                        }}
                        size="md"
                        borderRadius="md"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                        flex={1}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          updateFormData(
                            'startDate',
                            `${tomorrow.getFullYear()}-${String(
                              tomorrow.getMonth() + 1,
                            ).padStart(2, '0')}-${String(
                              tomorrow.getDate(),
                            ).padStart(2, '0')}`,
                          );
                        }}
                        title="Set start date to tomorrow"
                      >
                        Tomorrow
                      </Button>
                    </HStack>
                  </Box>
                  <Box flex="1">
                    <Text
                      fontSize="0.875rem"
                      mb="0.5rem"
                      fontWeight="500"
                      color="gray.700"
                    >
                      End Date (Optional)
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      color="gray.600"
                      mb="0.5rem"
                    >
                      When this absence ends. Leave empty for
                      indefinite absence. Must be after start date if
                      provided.
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        type="date"
                        value={absenceFormData.endDate}
                        onChange={(e) => {
                          updateFormData('endDate', e.target.value);
                        }}
                        size="md"
                        borderRadius="md"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                        flex={1}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (absenceFormData.startDate) {
                            const startDate = new Date(
                              absenceFormData.startDate,
                            );
                            const weekLater = new Date(startDate);
                            weekLater.setDate(
                              weekLater.getDate() + 7,
                            );

                            updateFormData(
                              'endDate',
                              `${weekLater.getFullYear()}-${String(
                                weekLater.getMonth() + 1,
                              ).padStart(2, '0')}-${String(
                                weekLater.getDate(),
                              ).padStart(2, '0')}`,
                            );
                          }
                        }}
                        isDisabled={!absenceFormData.startDate}
                        title="Set end date to 1 week from start date"
                      >
                        +1 Week
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          updateFormData('endDate', '');
                        }}
                        title="Clear end date (indefinite absence)"
                      >
                        Clear
                      </Button>
                    </HStack>
                  </Box>
                </HStack>

                <HStack align="flex-end" spacing="1rem">
                  <Box flex="1">
                    <Text
                      fontSize="0.875rem"
                      mb="0.5rem"
                      fontWeight="500"
                      color="gray.700"
                    >
                      Start Time (Optional)
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      color="gray.600"
                      mb="0.5rem"
                    >
                      Leave empty if absence is for the entire day.
                    </Text>
                    <HStack spacing={2}>
                      <ReactSelect<TimeOption>
                        options={timeOptions}
                        value={timeOptions.find(
                          (option) =>
                            option.value ===
                            absenceFormData.startTime,
                        )}
                        onChange={(selectedOption) =>
                          updateFormData(
                            'startTime',
                            selectedOption?.value || '',
                          )
                        }
                        isDisabled={!absenceFormData.startDate}
                        placeholder="Select Start Time"
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '40px',
                            minWidth: '100%',
                            width: '100%',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              border: '1px solid #cbd5e0',
                            },
                            '&:focus-within': {
                              border: '1px solid #3182ce',
                              boxShadow: '0 0 0 1px #3182ce',
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            fontSize: '0.875rem',
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : state.isFocused
                                ? '#e6f3ff'
                                : 'white',
                            color: state.isSelected
                              ? 'white'
                              : '#2d3748',
                            fontSize: '0.875rem',
                            padding: '0.5rem 0.75rem',
                            '&:hover': {
                              backgroundColor: state.isSelected
                                ? '#3182ce'
                                : '#e6f3ff',
                            },
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#2d3748',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            fontSize: '0.875rem',
                          }),
                          input: (provided) => ({
                            ...provided,
                            fontSize: '0.875rem',
                          }),
                          indicatorSeparator: (provided) => ({
                            ...provided,
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            '&:hover': {
                              color: '#4a5568',
                            },
                          }),
                        }}
                      />
                    </HStack>
                  </Box>
                  <Box flex="1">
                    <Text
                      fontSize="0.875rem"
                      mb="0.5rem"
                      fontWeight="500"
                      color="gray.700"
                    >
                      End Time (Optional)
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      color="gray.600"
                      mb="0.5rem"
                    >
                      Leave empty if absence is for the entire day.
                    </Text>
                    <HStack spacing={2}>
                      <ReactSelect<TimeOption>
                        options={timeOptions}
                        value={timeOptions.find(
                          (option) =>
                            option.value === absenceFormData.endTime,
                        )}
                        onChange={(selectedOption) =>
                          updateFormData(
                            'endTime',
                            selectedOption?.value || '',
                          )
                        }
                        isDisabled={!absenceFormData.startDate}
                        placeholder="Select End Time"
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided) => ({
                            ...provided,
                            minHeight: '40px',
                            minWidth: '100%',
                            width: '100%',
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            boxShadow: 'none',
                            fontSize: '0.875rem',
                            '&:hover': {
                              border: '1px solid #cbd5e0',
                            },
                            '&:focus-within': {
                              border: '1px solid #3182ce',
                              boxShadow: '0 0 0 1px #3182ce',
                            },
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999,
                            fontSize: '0.875rem',
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3182ce'
                              : state.isFocused
                                ? '#e6f3ff'
                                : 'white',
                            color: state.isSelected
                              ? 'white'
                              : '#2d3748',
                            fontSize: '0.875rem',
                            padding: '0.5rem 0.75rem',
                            '&:hover': {
                              backgroundColor: state.isSelected
                                ? '#3182ce'
                                : '#e6f3ff',
                            },
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#2d3748',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            fontSize: '0.875rem',
                          }),
                          input: (provided) => ({
                            ...provided,
                            fontSize: '0.875rem',
                          }),
                          indicatorSeparator: (provided) => ({
                            ...provided,
                            display: 'none',
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#a0aec0',
                            '&:hover': {
                              color: '#4a5568',
                            },
                          }),
                        }}
                      />
                    </HStack>
                  </Box>
                </HStack>

                {(canEditDisplayDate() || isAdmin()) && (
                  <Box>
                    <Text
                      fontSize="0.875rem"
                      mb="0.5rem"
                      fontWeight="500"
                      color="gray.700"
                    >
                      Display Date (Optional)
                    </Text>
                    <Text
                      fontSize="0.75rem"
                      color="gray.600"
                      mb="0.5rem"
                    >
                      When to start showing this absence. Can be
                      before the start date. Leave empty to show
                      immediately.
                    </Text>
                    <HStack spacing={2}>
                      <Input
                        type="date"
                        value={absenceFormData.displayDate || ''}
                        onChange={(e) => {
                          updateFormData(
                            'displayDate',
                            e.target.value || '',
                          );
                        }}
                        size="md"
                        borderRadius="md"
                        bg="white"
                        border="1px solid"
                        borderColor="gray.200"
                        _hover={{ borderColor: 'gray.300' }}
                        _focus={{
                          borderColor: 'blue.500',
                          boxShadow: '0 0 0 1px #3182ce',
                        }}
                        flex={1}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          updateFormData('displayDate', '');
                        }}
                        title="Clear display date"
                      >
                        Clear
                      </Button>
                    </HStack>
                  </Box>
                )}

                {/* Notes Field */}
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Covering Providers (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Search and select providers who will cover during
                    this absence.
                  </Text>
                  <ProviderSearchSelect
                    onProvidersChange={setCoveringProviders}
                    selectedProviders={coveringProviders}
                    placeholder="Search for covering providers..."
                  />
                </Box>

                {/* Notes Field */}
                <Box>
                  <Text
                    fontSize="0.875rem"
                    mb="0.5rem"
                    fontWeight="500"
                    color="gray.700"
                  >
                    Notes (Optional)
                  </Text>
                  <Text
                    fontSize="0.75rem"
                    color="gray.600"
                    mb="0.5rem"
                  >
                    Optional: Additional information about this
                    absence.
                  </Text>
                  <Box position="relative">
                    <RichTextEditor
                      value={absenceFormData.note || ''}
                      onChange={(value) => {
                        setValidationError(null);
                        setAbsenceFormData({
                          ...absenceFormData,
                          note: value || undefined,
                        });
                      }}
                      placeholder="Optional: Enter absence notes..."
                      minHeight="8rem"
                      maxHeight="20rem"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setAbsenceFormData({
                          ...absenceFormData,
                          note: undefined,
                        });
                      }}
                      title="Clear notes"
                      position="absolute"
                      top="0.5rem"
                      right="0.5rem"
                      zIndex={10}
                    >
                      Clear
                    </Button>
                  </Box>
                </Box>

                {validationError && (
                  <Alert
                    status="error"
                    borderRadius="md"
                    fontSize="0.875rem"
                  >
                    <AlertIcon />
                    {validationError}
                  </Alert>
                )}
              </VStack>
            </Box>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            bg="yellow.50"
            borderBottomRadius="xl"
          >
            <HStack spacing="1rem">
              <Button
                variant="outline"
                onClick={closeModal}
                size="md"
                borderRadius="md"
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={async () => {
                  // Validate dates using utility function
                  const dateValidationResult = validateAbsenceDates(
                    absenceFormData.startDate,
                    absenceFormData.endDate || undefined,
                    absenceFormData.displayDate,
                  );

                  if (!dateValidationResult.isValid) {
                    setValidationError(
                      dateValidationResult.errorMessage ||
                        'Date validation failed.',
                    );
                    return;
                  }

                  // Centralized time validation rules
                  const startTimeVal =
                    absenceFormData.startTime || '';
                  const endTimeVal = absenceFormData.endTime || '';
                  const hasStartTime = startTimeVal !== '';
                  const hasEndTime = endTimeVal !== '';

                  if (hasStartTime && hasEndTime) {
                    // Check if this is a cross-midnight scenario
                    const startDate = new Date(
                      absenceFormData.startDate,
                    );

                    // For single-day absences, use start date as end date
                    const endDate = absenceFormData.endDate
                      ? new Date(absenceFormData.endDate)
                      : startDate;

                    const isSameDay =
                      startDate.toDateString() ===
                      endDate.toDateString();

                    if (isSameDay) {
                      // Same day: check if it's an overnight shift
                      const isOvernight = isOvernightShift(
                        startTimeVal,
                        endTimeVal,
                      );

                      if (!isOvernight) {
                        // Not overnight: end time must be after start time
                        const startTimeMinutes =
                          convertTimeToMinutes(startTimeVal);
                        const endTimeMinutes =
                          convertTimeToMinutes(endTimeVal);

                        if (startTimeMinutes >= endTimeMinutes) {
                          setValidationError(
                            'End Time must be after Start Time for same-day absences.',
                          );
                          return;
                        }
                      }
                      // If it's an overnight shift, no validation needed (11 PM to 1 AM is valid)
                    } else {
                      // Different days: times can be any order (cross-midnight scenarios)
                      // No validation needed for cross-midnight
                    }
                  } else if (hasStartTime !== hasEndTime) {
                    setValidationError(
                      'Provide both Start Time and End Time, or leave both empty for an all‑day absence.',
                    );
                    return;
                  }

                  await handleSaveAbsence();
                }}
                isLoading={isSaving}
                loadingText="Saving..."
                isDisabled={
                  ('id' in absenceFormData && absenceFormData.id
                    ? selectedPracticeIds.length === 0 &&
                      !absenceFormData.practiceId
                    : selectedPracticeIds.length === 0) ||
                  !absenceFormData.startDate ||
                  isSaving
                }
                size="md"
                borderRadius="md"
              >
                {'id' in absenceFormData && absenceFormData.id
                  ? 'Update Absence'
                  : 'Save Absence'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="md"
        isCentered
      >
        <ModalOverlay
          bg="blackAlpha.300"
          backdropFilter="blur(10px)"
        />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader
            borderBottom="1px solid"
            borderColor="gray.200"
            pb="1rem"
            bg="yellow.50"
            borderTopRadius="xl"
          >
            <Text fontSize="1.25rem" fontWeight="600">
              Delete Absence
            </Text>
          </ModalHeader>
          <ModalBody p="2rem">
            <Text fontSize="0.875rem" color="gray.700">
              Are you sure you want to delete this absence? This
              action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter
            borderTop="1px solid"
            borderColor="gray.200"
            pt="1rem"
            bg="yellow.50"
            borderBottomRadius="xl"
          >
            <HStack spacing="1rem">
              <Button
                variant="outline"
                onClick={() => setIsDeleteModalOpen(false)}
                size="md"
                borderRadius="md"
                isDisabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    if (absenceToDelete) {
                      await handleDeleteAbsence(absenceToDelete);
                    }
                  } catch (error) {
                    // Error handling is done in handleDeleteAbsence
                  } finally {
                    setIsDeleting(false);
                    setIsDeleteModalOpen(false);
                  }
                }}
                isLoading={isDeleting}
                loadingText="Deleting..."
                isDisabled={isDeleting}
                size="md"
                borderRadius="md"
              >
                Delete
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProviderAbsencesCard;
